type DateLike = string | number | Date | null | undefined;

const DATE_SEARCH_PATTERN = /^[0-9:/\-\s]+$/;

function padTwo(value: number) {
  return String(value).padStart(2, "0");
}

function parseDateLike(value: DateLike): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  if (typeof value === "number") {
    const fromNumber = new Date(value);
    return Number.isFinite(fromNumber.getTime()) ? fromNumber : null;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  const fromNative = new Date(trimmed);
  if (Number.isFinite(fromNative.getTime())) {
    return fromNative;
  }

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
      0,
      0,
      0
    );
  }

  if (trimmed.includes(" ")) {
    const fromIsoLike = new Date(trimmed.replace(" ", "T"));
    if (Number.isFinite(fromIsoLike.getTime())) {
      return fromIsoLike;
    }
  }

  return null;
}

export function formatAppDateTime(value: DateLike, fallback = "—") {
  const parsed = parseDateLike(value);
  if (!parsed) return fallback;
  const year = parsed.getFullYear();
  const month = padTwo(parsed.getMonth() + 1);
  const day = padTwo(parsed.getDate());
  const hour = padTwo(parsed.getHours());
  const minute = padTwo(parsed.getMinutes());
  const second = padTwo(parsed.getSeconds());
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

export function formatCurrentDateTime() {
  return formatAppDateTime(new Date(), "");
}

export function formatDateFilterDisplay(value: string | null | undefined, fallback = "All") {
  if (!value) return fallback;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return formatAppDateTime(value, value);
}

export function normalizeDateFilterInput(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const withOptionalSeconds = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
  if (withOptionalSeconds) {
    const [, date, hh, mm, ss] = withOptionalSeconds;
    return `${date} ${hh}:${mm}:${ss || "00"}`;
  }

  const parsed = formatAppDateTime(trimmed, "");
  return parsed || trimmed;
}

export function isDateLikeSearchTerm(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (!DATE_SEARCH_PATTERN.test(trimmed)) return false;
  return trimmed.replace(/[^0-9]/g, "").length >= 4;
}

export function matchesDateFilter(timestamp: DateLike, dateFilter: string) {
  const formatted = formatAppDateTime(timestamp, "");
  if (formatted) {
    return formatted.startsWith(dateFilter);
  }
  if (typeof timestamp === "string") {
    return timestamp.startsWith(dateFilter);
  }
  return false;
}

export function matchesDateSearch(timestamp: DateLike, searchTerm: string) {
  const search = searchTerm.trim();
  if (!search) return true;

  const formatted = formatAppDateTime(timestamp, "");
  const canonical = formatted || (typeof timestamp === "string" ? timestamp : "");
  if (!canonical) return false;

  const loweredCanonical = canonical.toLowerCase();
  const loweredSearch = search.toLowerCase();
  if (loweredCanonical.includes(loweredSearch)) return true;

  const compactCanonical = canonical.replace(/[^0-9]/g, "");
  const compactSearch = search.replace(/[^0-9]/g, "");
  if (!compactSearch) return false;
  return compactCanonical.startsWith(compactSearch);
}
